import { Controller } from "@hotwired/stimulus"

// Public "Which budget fits me" tool.
//
// It does not invent a budgeting method and it does not advise. The four
// cards are BudgetFramework::FRAMEWORKS — the presets the onboarding budget
// slide offers and OnboardingPersistence turns into budgets — rendered server
// side. All this does is measure what share of the income the entered
// essentials take and mark which framework's own split still fits those
// numbers, then show every framework in money.
//
// The wording that goes with it is deliberately descriptive: the page reports
// what the entered amounts match, never what somebody should do. Nothing here
// is personalised financial advice.
//
// The bars are the horizontal twin of the income tool's vials: same drag
// maths, same step, same liquid, turned ninety degrees.
export default class extends Controller {
  static targets = [
    "income", "need", "bar", "fill", "wash", "grip", "rail", "card",
    "verdict", "verdictSub", "needsBar", "needsPct", "needsAmount"
  ]

  // Same round-number step the assign screen uses.
  static STEP = 10

  connect() {
    this.reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.lastMatch = null
    this.paint()
  }

  disconnect() {
    cancelAnimationFrame(this.slideFrame)
    clearTimeout(this.snapTimer)
  }

  // ── Numbers ──────────────────────────────────────────────────────────────

  parse(value) {
    return parseInt(String(value).replace(/[^\d]/g, ""), 10) || 0
  }

  money(value) {
    return `$${Math.round(value).toLocaleString("en-US")}`
  }

  income() {
    return this.parse(this.incomeTarget.value)
  }

  needs() {
    return this.needTargets.reduce((acc, input) => acc + this.parse(input.value), 0)
  }

  formatIncome() {
    this.incomeTarget.value = this.income().toLocaleString("en-US")
    this.paint()
  }

  formatNeed(event) {
    event.target.value = this.parse(event.target.value).toLocaleString("en-US")
    this.paint()
  }

  selectNeed(event) {
    event.target.select()
  }

  setNeed(index, value) {
    this.needTargets[index].value = Math.max(0, value).toLocaleString("en-US")
    this.paint()
  }

  // ── Drag ─────────────────────────────────────────────────────────────────

  dragStart(event) {
    // The amount is its own control; a press that lands there is a click into
    // the field rather than the start of a drag.
    if (event.target.closest(".tool-bar-input")) return
    event.preventDefault()

    const bar   = event.currentTarget
    const index = Number(bar.dataset.index)
    const rect  = bar.getBoundingClientRect()
    // A full bar is the whole income: dragging to the end means this one line
    // eats everything, which is exactly the case the page has to be able to
    // show. Never zero, so an empty income still drags.
    const dragMax = Math.max(1, this.income())
    const rtl     = getComputedStyle(bar).direction === "rtl"

    bar.setPointerCapture(event.pointerId)
    bar.classList.add("grabbing")

    const move = (ev) => {
      let fraction = (ev.clientX - rect.left) / rect.width
      if (rtl) fraction = 1 - fraction
      fraction = Math.max(0, Math.min(1, fraction))
      const step = this.constructor.STEP
      this.setNeed(index, Math.round((fraction * dragMax) / step) * step)
    }
    const stop = () => {
      bar.classList.remove("grabbing")
      bar.removeEventListener("pointermove", move)
      bar.removeEventListener("pointerup", stop)
      bar.removeEventListener("pointercancel", stop)
    }

    bar.addEventListener("pointermove", move)
    bar.addEventListener("pointerup", stop)
    bar.addEventListener("pointercancel", stop)
    move(event)
  }

  // ── Which framework still fits ───────────────────────────────────────────
  //
  // Read off the frameworks themselves: 50/30/20 only works while needs fit in
  // half the income, 70/20/10 while they fit in seventy percent, 80/20 while
  // there is still a fifth to put aside. Past that no split matches, so the
  // single budget is what is left.
  matching(share) {
    if (share <= 0.5) return "50_30_20"
    if (share <= 0.7) return "70_20_10"
    if (share <= 0.8) return "80_20"
    return "single"
  }

  paint() {
    const income = this.income()
    const needs  = this.needs()
    const share  = income > 0 ? needs / income : 0
    const pct    = Math.round(share * 100)

    this.paintBars(income)

    this.needsAmountTarget.textContent = this.money(needs)
    this.needsPctTarget.textContent = income > 0 ? `${pct}%` : "—"
    this.needsBarTarget.style.width = `${Math.min(100, pct)}%`
    this.needsBarTarget.classList.toggle("is-tight", share > 0.7)
    this.needsBarTarget.classList.toggle("is-warn", share > 0.5 && share <= 0.7)

    if (income <= 0) {
      this.verdictTarget.textContent = "Add your monthly income"
      this.verdictSubTarget.textContent = "The amounts below fill in once there is an income to measure them against."
      this.cardTargets.forEach((card) => card.classList.remove("selected"))
      this.paintAmounts(0)
      return
    }

    const match  = this.matching(share)
    const chosen = this.cardTargets.find((card) => card.dataset.ruleId === match)
    this.cardTargets.forEach((card) => {
      card.classList.toggle("selected", card.dataset.ruleId === match)
    })

    // Only when the match actually changes. paint() runs on every frame of a
    // drag, and re-issuing the slide sixty times a second restarted the
    // animation from wherever it had got to, which is what made it stutter.
    if (match !== this.lastMatch) {
      this.lastMatch = match
      this.centre(chosen)
    }
    this.verdictTarget.textContent = `Your numbers match ${chosen.dataset.ruleStyle}`
    this.verdictSubTarget.textContent = this.explain(share, pct, needs > income)
    this.paintAmounts(income)
  }

  // Slides the rail so the matching card sits in the middle.
  //
  // Hand-rolled rather than scrollTo({ behavior: "smooth" }) for two reasons.
  // scrollTo also scrolls the page when the element cannot move further, which
  // would yank the bars out from under the finger doing the dragging. And a
  // mandatory snap container fights a programmatic smooth scroll: the snap
  // engine re-targets mid-flight and the card arrives in two jerks. Snapping
  // is switched off for the length of the tween and handed straight back, so
  // hand-scrolling still snaps.
  centre(card) {
    if (!this.hasRailTarget || !card) return

    const rail = this.railTarget
    const left = card.offsetLeft - (rail.clientWidth - card.clientWidth) / 2
    const from = rail.scrollLeft
    const delta = left - from

    cancelAnimationFrame(this.slideFrame)
    clearTimeout(this.snapTimer)

    if (Math.abs(delta) < 1) return
    if (this.reduceMotion) {
      rail.scrollLeft = left
      return
    }

    // Longer trips get a little more time, so a jump across three cards does
    // not have to move at the same speed as a nudge to the neighbour.
    const duration = Math.min(560, 280 + Math.abs(delta) * 0.35)
    const start = performance.now()
    rail.style.scrollSnapType = "none"

    const step = (now) => {
      const k = Math.min(1, (now - start) / duration)
      // Same curve the app's motion uses: quick off the mark, soft landing.
      const eased = 1 - Math.pow(1 - k, 3)
      rail.scrollLeft = from + delta * eased
      if (k < 1) {
        this.slideFrame = requestAnimationFrame(step)
      } else {
        // A frame of slack before snapping is handed back, so the engine reads
        // the settled position rather than the last animated one.
        this.snapTimer = setTimeout(() => { rail.style.scrollSnapType = "" }, 60)
      }
    }
    this.slideFrame = requestAnimationFrame(step)
  }

  // Describes the arithmetic, nothing else: what the entered figures leave and
  // which split still fits inside it.
  explain(share, pct, overspent) {
    if (overspent) {
      return `The amounts entered come to more than the income entered, so none of the four splits fits them.`
    }
    if (share <= 0.5) {
      return `The essentials entered take ${pct}% of the income entered, so they still fit inside a 50% needs share, and the other two splits fit as well.`
    }
    if (share <= 0.7) {
      return `The essentials entered take ${pct}% of the income entered. That is past a 50% needs share, and inside a 70% one.`
    }
    if (share <= 0.8) {
      return `The essentials entered take ${pct}% of the income entered. That is past a 70% needs share, leaving about a fifth for anything else.`
    }
    return `The essentials entered take ${pct}% of the income entered, so none of the three splits fits and everything sits in one budget.`
  }

  // The liquid tracks the share of income each line takes, so the bars and the
  // percentage under them tell the same story.
  paintBars(income) {
    this.needTargets.forEach((input, i) => {
      const value = this.parse(input.value)
      const pct = income > 0 ? Math.min(100, (value / income) * 100) : 0
      this.fillTargets[i].style.width = `${pct}%`
      this.washTargets[i].style.width = `${pct}%`
      this.gripTargets[i].style.insetInlineStart = `${pct}%`
      // Width tracks the digits so the $ stays against the number. Tabular
      // figures make ch a reliable unit here.
      input.style.width = `${Math.max(3, input.value.length)}ch`
    })
  }

  // Every slot carries its own share, so the amounts are the frameworks'
  // percentages against the income rather than anything new.
  paintAmounts(income) {
    this.element.querySelectorAll("[data-slot-pct]").forEach((node) => {
      const pct = Number(node.dataset.slotPct)
      node.textContent = income > 0 ? this.money((income * pct) / 100) : "—"
    })
  }
}
